import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SkipSelf,
  TemplateRef,
} from '@angular/core';

import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { LocalStorageService } from '@recruitee/common';
import {
  IconName,
  IconSize,
  RtInfoboxButtonVariant,
  RtStatefulComponent,
  IconColorDirective,
  MessageComponent,
  IconButtonComponent,
  ButtonComponent,
  GroupComponent,
  DividerComponent,
  BadgeComponent,
  HeadingComponent,
  IconComponent,
} from '@recruitee/user-interface';

type Visibility = 'visible' | 'hidden';

type State = {
  hidden: Visibility;
};

export type InfoboxButton = {
  text: string;
  action: string;
  variant: RtInfoboxButtonVariant;
};

export type InfoboxType = 'success' | 'information' | 'warning' | 'error' | 'transparent';

@Component({
  selector: 'rt-infobox',
  templateUrl: './infobox.component.html',
  styleUrls: ['./infobox.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hidden]': 'isHidden',
  },
  standalone: true,
  imports: [
    MessageComponent,
    IconColorDirective,
    IconComponent,
    NgTemplateOutlet,
    HeadingComponent,
    BadgeComponent,
    DividerComponent,
    GroupComponent,
    ButtonComponent,
    IconButtonComponent,
  ],
})
export class InfoboxComponent extends RtStatefulComponent<State> {
  private static PREFIX: string = 'infobox__';

  @Input() public storageKey: string;
  @Input() public isDismissible: boolean = true;
  @Input() public type: InfoboxType = 'information';
  @Input() public iconName: IconName;
  @Input() public iconSize: IconSize = 'medium';
  @Input() public closeIconSize: IconSize = 'small';
  @Input() public iconMarginRightSmall: boolean = false;
  @Input() public heading: string;
  @Input() public subheading: string;
  @Input() public subheadingTpl: TemplateRef<unknown>;
  @Input() public headingBadge: string;
  @Input() public buttons?: InfoboxButton[] = [];
  @Input() public rounded: boolean = true;
  @Input() public noBorder: boolean;
  @Input() public borderBottom: boolean;
  @Input() public messagePaddingSmall: boolean = false;
  @Input() public isBadgeBeforeHeading: boolean = false;
  @Input() public imageTemplate: TemplateRef<any>;
  @Input() public daysToRenewal: number;
  @Input() public hasDotDivider = true;

  @Output() public dismissClick: EventEmitter<MouseEvent> = new EventEmitter();

  public isHidden$: Observable<boolean>;

  constructor(
    private localStorageService: LocalStorageService,
    @SkipSelf() protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.createState({
      hidden: 'hidden',
    });
    this.isHidden$ = this.state$.pipe(map(state => state.hidden === 'hidden'));
  }

  public ngOnInit(): void {
    this.setState({ ...this.state, hidden: this.getInfoboxVisibility()! });
  }

  public get isHidden(): boolean {
    return this.state.hidden === 'hidden';
  }

  public get messageVariant(): string {
    return `context-${this.type}`;
  }

  public closeInfoBox(event: MouseEvent): void {
    this.setState({
      ...this.state,
      ...{
        hidden: 'hidden',
      },
    });
    this.saveInfoboxVisibility();
    this.dismissClick.emit(event);
  }

  public trackBy(index: number, item: InfoboxButton): string {
    return item.action;
  }

  private get componentStorageKey(): string {
    return InfoboxComponent.PREFIX + this.storageKey;
  }

  private saveInfoboxVisibility(): void {
    if (this.storageKey) {
      this.localStorageService.set(
        this.componentStorageKey,
        !!this.daysToRenewal
          ? {
              hidden: this.state.hidden,
              renewal: moment().add(this.daysToRenewal, 'days').valueOf(),
            }
          : {
              hidden: this.state.hidden,
            },
      );
    }
  }

  private getInfoboxVisibility(): Visibility | undefined {
    const boxStateData = this.localStorageService.get(this.componentStorageKey);

    if (!boxStateData) return undefined;

    if (boxStateData.renewal) {
      const now = moment();
      if (now.valueOf() > boxStateData.renewal) {
        this.localStorageService.remove(this.componentStorageKey);
        return undefined;
      }
    }
    return boxStateData.hidden;
  }
}
